import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { COLORS } from "../../../constants";
import { CloseIcon } from "../../Icons";

import { ModalHeader } from "./components/ModalHeader";

export { ModalBody } from "./components/ModalBody";
export { ModalButton } from "./components/ModalButton";
export { ModalCancelButton } from "./components/ModalCancelButton";
export { ModalFooter } from "./components/ModalFooter";
export { ModalHeader } from "./components/ModalHeader";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
`;

const Dialog = styled.div<{ $width: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: ${({ $width }) => $width}px;
  max-width: calc(100vw - 32px);
  padding: 20px;
  border-radius: 8px;
  background: ${COLORS.white};
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08), 0 16px 12px rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${COLORS.black45};
  transition: color 0.2s;

  &:hover {
    color: ${COLORS.black85};
  }
`;

interface ModalProps {
  visible: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  width?: number;
  /** Hide the top-right close (X) — e.g. when the footer already has a Cancel/Close action. */
  hideCloseButton?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  title,
  children,
  width = 480,
  hideCloseButton = false,
}) => {
  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, onClose]);

  if (!visible) {
    return null;
  }

  return createPortal(
    <Backdrop onClick={onClose}>
      <Dialog $width={width} onClick={e => e.stopPropagation()}>
        <Header>
          {title != null && <ModalHeader>{title}</ModalHeader>}
          {!hideCloseButton && (
            <CloseButton type="button" aria-label="Close" onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          )}
        </Header>
        {children}
      </Dialog>
    </Backdrop>,
    document.body
  );
};
